import { useEffect, useState } from "react";
import { api, leerSesion } from "../api.ts";
import { aLocal, PRIVACIDADES_AGENDA } from "../../../dominio/src/index.ts";
import { AvisoError, Seccion, useAccion } from "./comun.tsx";

interface Estado {
  disponible: boolean;
  enlace: { url: string; alcance: "mias" | "clinica"; privacidad: string; creado: string; ultimoUso: string | null; usos: number } | null;
}

/** Ver la agenda de Donna en el Google Calendar (o Apple, Outlook) del propio profesional: enlace secreto, de solo lectura. */
export function AgendaGoogle({ cerrar }: { cerrar: () => void }) {
  const [e, setE] = useState<Estado | null>(null);
  const [privacidad, setPrivacidad] = useState("iniciales");
  const [alcance, setAlcance] = useState<"mias" | "clinica">("mias");
  const [copiado, setCopiado] = useState(false);
  const a = useAccion();
  // el profesional con consulta solo ve sus propias citas; recepción y administración pueden incluir las de toda la clínica
  const rol = leerSesion()?.usuario.rol;
  const veTodas = rol !== "odontologo" && rol !== "especialista";
  const cargar = async () => { const r = await a.correr(() => api<Estado>("GET", "/api/agenda-externa")); if (r) { setE(r); if (r.enlace) { setPrivacidad(r.enlace.privacidad); setAlcance(r.enlace.alcance); } } };
  useEffect(() => { cargar(); }, []);

  // Sincronización directa con Google (solo aparece cuando el servidor la tiene activada)
  const [g, setG] = useState<{ habilitado: boolean; conectado?: boolean; correo?: string; privacidad?: string; alcance?: "mias" | "clinica"; ultimoSync?: string | null; error?: string; activo?: boolean } | null>(null);
  useEffect(() => { api<NonNullable<typeof g>>("GET", "/api/google-calendario").then(setG).catch(() => setG(null)); }, []);
  async function conectarGoogle() {
    const r = await a.correr(() => api<{ url: string }>("POST", "/api/google-calendario/conectar", { privacidad, alcance }));
    if (r) location.href = r.url;
  }
  async function sincronizarGoogle() { const r = await a.correr(() => api<{ estado: NonNullable<typeof g> }>("POST", "/api/google-calendario/sincronizar")); if (r) setG(r.estado); }
  async function desconectarGoogle() {
    if (!confirm("Se borrará el calendario «Donna · Agenda» de tu Google y Donna dejará de enviarle citas. ¿Desconectar?")) return;
    const r = await a.correr(() => api<NonNullable<typeof g>>("DELETE", "/api/google-calendario"));
    if (r) setG(r);
  }

  async function crear() {
    const nuevo = !!e?.enlace;
    if (nuevo && !confirm("Se creará un enlace nuevo y el anterior dejará de funcionar. Tendrás que pegar el nuevo en tu calendario. ¿Continuar?")) return;
    const r = await a.correr(() => api<Estado>("POST", "/api/agenda-externa", { privacidad, alcance }));
    if (r) { setE(r); setCopiado(false); }
  }
  async function quitar() {
    if (!confirm("Tu calendario dejará de recibir las citas de Donna. Las que ya se copiaron desaparecen en unas horas. ¿Desactivar el enlace?")) return;
    const r = await a.correr(() => api<Estado>("DELETE", "/api/agenda-externa"));
    if (r) setE(r);
  }
  async function copiar() {
    if (!e?.enlace) return;
    try { await navigator.clipboard.writeText(e.enlace.url); setCopiado(true); } catch { setCopiado(false); }
  }

  const enlace = e?.enlace;
  const ultimo = enlace?.ultimoUso ? `${aLocal(new Date(enlace.ultimoUso)).fecha} ${aLocal(new Date(enlace.ultimoUso)).hora}` : "";
  return (
    <Seccion titulo="Mi agenda en Google Calendar" acciones={<button className="btn chico" onClick={cerrar}>Cerrar</button>}>
      <AvisoError error={a.error} />
      {e && !e.disponible && (
        <div className="aviso info" role="status">Esta opción funciona en la versión en línea de Donna: Google necesita poder abrir el enlace desde internet. En esta instalación no está disponible.</div>
      )}
      {e?.disponible && (
        <>
          <p className="muted" style={{ margin: 0, fontSize: 13 }}>
            Crea un enlace <b>secreto y de solo lectura</b> y pégalo una sola vez en <b>tu propio</b> Google Calendar: tus citas aparecerán en tu cuenta y en tu celular.
            Los cambios que hagas en Donna llegan a Google en unas horas (Google decide cuándo actualiza). Lo que cambies en Google <b>no</b> vuelve a Donna.
          </p>
          <div className="fila" style={{ flexWrap: "wrap", gap: 12 }}>
            <label style={{ display: "grid", gap: 4 }}>
              <span style={{ fontSize: 13 }}>Qué se ve de cada cita</span>
              <select className="entrada" value={privacidad} onChange={(x) => setPrivacidad(x.target.value)}>
                {PRIVACIDADES_AGENDA.map(([k, t]) => <option key={k} value={k}>{t}</option>)}
              </select>
            </label>
            {veTodas && (
              <label style={{ display: "grid", gap: 4 }}>
                <span style={{ fontSize: 13 }}>Qué citas</span>
                <select className="entrada" value={alcance} onChange={(x) => setAlcance(x.target.value as "mias" | "clinica")}>
                  <option value="mias">Solo las mías</option><option value="clinica">Toda la clínica</option>
                </select>
              </label>
            )}
          </div>
          <p className="muted" style={{ margin: 0, fontSize: 12 }}>
            Nunca se envía el motivo de la cita, las notas ni datos clínicos. Aun así, tu calendario de Google queda en tu cuenta: usa «solo Cita» si lo compartes con otras personas.
          </p>
          <div className="fila" style={{ flexWrap: "wrap" }}>
            <button className="btn pri" onClick={crear} disabled={a.ocupado}>{enlace ? "Crear enlace nuevo" : "Crear mi enlace"}</button>
            {enlace && <button className="btn" onClick={quitar} disabled={a.ocupado}>Desactivar</button>}
          </div>
          {enlace && (
            <div className="aviso ok" role="status">
              <b>Tu enlace (no lo compartas):</b>
              <input className="entrada" readOnly value={enlace.url} onFocus={(x) => x.currentTarget.select()} style={{ width: "100%", marginTop: 6 }} aria-label="Enlace de tu agenda" />
              <div className="fila" style={{ marginTop: 6, flexWrap: "wrap" }}>
                <button className="btn chico" onClick={copiar}>{copiado ? "✓ Copiado" : "Copiar enlace"}</button>
                <span className="muted" style={{ fontSize: 12 }}>{enlace.usos ? `Google lo ha leído ${enlace.usos} ${enlace.usos === 1 ? "vez" : "veces"}${ultimo ? ` · la última, el ${ultimo}` : ""}.` : "Todavía no lo ha leído nadie."}</span>
              </div>
              <ol style={{ margin: "10px 0 0", paddingLeft: 18, fontSize: 13 }}>
                <li>Abre <b>calendar.google.com</b> en una computadora (no en el celular).</li>
                <li>A la izquierda, junto a «Otros calendarios», toca <b>+</b> y elige <b>Desde URL</b>.</li>
                <li>Pega el enlace y toca <b>Agregar calendario</b>. En el celular aparecerá solo, con la misma cuenta.</li>
              </ol>
            </div>
          )}
        </>
      )}
      {g?.habilitado && (
        <div className="aviso info" role="status">
          <b>Conexión directa con tu Google</b>
          {g.conectado ? (
            <>
              <p style={{ margin: "6px 0" }}>Conectada a <b>{g.correo || "tu cuenta de Google"}</b>. Donna mantiene al día un calendario propio llamado «Donna · Agenda».
                {g.ultimoSync ? ` Última actualización: ${aLocal(new Date(g.ultimoSync)).fecha} ${aLocal(new Date(g.ultimoSync)).hora}.` : ""}</p>
              {g.error && <p className="muted" style={{ margin: "6px 0" }}>Aviso: {g.error}{g.activo === false ? " Vuelve a conectar tu cuenta." : ""}</p>}
              <div className="fila" style={{ flexWrap: "wrap" }}>
                <button className="btn chico" onClick={sincronizarGoogle} disabled={a.ocupado}>Actualizar ahora</button>
                <button className="btn chico" onClick={desconectarGoogle} disabled={a.ocupado}>Desconectar</button>
              </div>
            </>
          ) : (
            <>
              <p style={{ margin: "6px 0" }}>Autoriza a Donna una sola vez y tus citas aparecerán solas en tu Google Calendar, con los cambios en minutos. Donna solo toca el calendario que ella crea; el resto de tu calendario no lo ve.</p>
              <button className="btn pri" onClick={conectarGoogle} disabled={a.ocupado}>Conectar con Google</button>
            </>
          )}
        </div>
      )}
    </Seccion>
  );
}
